import React, {
  useState, useEffect, useMemo,
} from 'react';
import Observer from '@researchgate/react-intersection-observer';
import _debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
// Core components
import Skeleton from 'components/core/Skeleton';
import TableRow from 'components/core/table/TableRow';
import TableCell from 'components/core/table/TableCell';
// Custom
import { TABLE_ROW_HEIGHT } from 'constants/App';

const ListTemplateRow = React.memo((
  {
    rowComp: RowComp, rowCompDetails: RowCompDetails, name, element, columnsQuantity,
    withSelection, selected, withChoose, chooseTooltipId,
    withActions, nbActions, withDetails, showDetails, withVirtualization,
    onShowHideDetails, onClick, onDoubleClick,
    waitForDisplay, onAskForDisplay,
  },
) => {
  // Manage visible status
  const [visible, setVisible] = useState(!withVirtualization);
  const [firstDisplayDone, setFirstDisplayDone] = useState(!withVirtualization);

  // Determine if a first displayed has been done
  useEffect(() => {
    if (!firstDisplayDone && visible && !waitForDisplay) {
      setFirstDisplayDone(true);
    }
  }, [firstDisplayDone, visible, waitForDisplay]);

  // Debounce treatment to be sure that the row is visible
  const handleObserverChange = _debounce((entry, unobserve) => {
    if (entry.isIntersecting) {
      onAskForDisplay();
      setVisible(true);
      // Once row is visible, stop observing it
      unobserve();
    }
  }, 10);

  // Displayable flag
  const displayable = visible && (firstDisplayDone || !waitForDisplay);

  // Memoize row specs
  const rowSpecs = useMemo(() => ({
    withSelection,
    selected,
    withChoose,
    chooseTooltipId,
    withActions,
    nbActions,
    withDetails,
    showDetails,
    onShowHideDetails,
    onClick,
    onDoubleClick,
  }), [withSelection, selected, withChoose, chooseTooltipId, withActions, nbActions, withDetails, showDetails, onShowHideDetails, onClick, onDoubleClick]);

  return (
    <React.Fragment>
      {!displayable ? (
        <Observer
          onChange={handleObserverChange}
          disabled={!withVirtualization}
          rootMargin={`0px 0px ${TABLE_ROW_HEIGHT * 5}px 0px`}
        >
          <TableRow rowId={element.id}>
            <TableCell colSpan={columnsQuantity}>
              <Skeleton />
            </TableCell>
          </TableRow>
        </Observer>
      ) : (
        <RowComp
          {...{ [name]: element }}
          rowSpecs={rowSpecs}
        />
      )}
      {showDetails && (
        <TableRow hover={false}>
          <TableCell colSpan={columnsQuantity} className="table-row-details">
            <RowCompDetails {...{ [name]: element }} />
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
});

ListTemplateRow.displayName = 'ListTemplateRow';

ListTemplateRow.propTypes = {
  rowComp: PropTypes.elementType.isRequired,
  rowCompDetails: PropTypes.elementType,
  name: PropTypes.string.isRequired,
  element: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  columnsQuantity: PropTypes.number.isRequired,
  withSelection: PropTypes.bool.isRequired,
  selected: PropTypes.bool.isRequired,
  withChoose: PropTypes.bool.isRequired,
  chooseTooltipId: PropTypes.string,
  withActions: PropTypes.bool.isRequired,
  nbActions: PropTypes.number.isRequired,
  withDetails: PropTypes.bool.isRequired,
  showDetails: PropTypes.bool.isRequired,
  withVirtualization: PropTypes.bool.isRequired,
  onShowHideDetails: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
  waitForDisplay: PropTypes.bool.isRequired,
  onAskForDisplay: PropTypes.func.isRequired,
};

ListTemplateRow.defaultProps = {
  rowCompDetails: null,
  chooseTooltipId: null,
};

export default ListTemplateRow;



// WEBPACK FOOTER //
// ./components/templates/ListTemplateRow.js